import React, { useCallback, useEffect, useRef, useState } from "react";
import { FacetSectionHeader } from "./FacetSectionHeader";

/**
 * RangeSection — a min/max filter over one numeric attribute (size, duration).
 *
 * Replaces NumericSliderSection, which had three problems that made it closer to
 * decoration than a control:
 *
 *   1. It took a `formatter` prop and never used it. The readouts were raw
 *      <input type="number"> over the stored unit, so filtering to "10 MB" meant
 *      typing 10485760, and the Duration box showed 3661 rather than 1:01:01.
 *   2. It applied only on mouseup/touchend. Arrow keys move a range input, so a
 *      keyboard user could move both thumbs and never filter anything.
 *   3. Its thumb stacking was `zIndex: min > absMax - 100 ? 5 : 3` — a constant
 *      in VALUE units, so it read as "always" on a 60-second range and "never"
 *      on a range of bytes. When both thumbs landed on the same spot the wrong
 *      one was on top and the other could not be grabbed.
 *
 * Presets exist because a linear slider is a poor instrument for file sizes:
 * most of a workspace sits in the first fraction of a percent of the track. The
 * common cuts are one click, and the slider is there for the rest.
 *
 * Props:
 *   field        — the Algolia attribute ("metadata.size")
 *   value        — { min, max } currently applied, or {}
 *   stats        — { min, max } from Algolia's facet stats; absent → no section
 *   format       — (n) => string, for readouts and a11y announcements
 *   parse        — (string) => number|null, the inverse; null rejects the input
 *   presets      — [{ label, min, max }] one-click common ranges
 *   onApply      — (field, min, max) => void
 *   onClear      — (field) => void
 */

// Long enough that dragging a thumb doesn't fire a query per pixel, short
// enough that letting go feels immediate.
const APPLY_DELAY_MS = 220;

export function RangeSection({
    title,
    field,
    value = {},
    stats,
    format = (v) => String(v),
    parse = (v) => (Number.isFinite(Number(v)) ? Number(v) : null),
    presets = [],
    defaultExpanded = false,
    onApply,
    onClear,
}) {
    const hasActiveFilter = value.min !== undefined || value.max !== undefined;

    const [expanded, setExpanded] = useState(defaultExpanded || hasActiveFilter);

    // M-F2: track the stats directly so bounds can tighten again (a Math.min /
    // Math.max accumulator only ever widened, going stale across workspace
    // switches). While OUR OWN filter is active the stats describe the filtered
    // hits, so the bounds are frozen — otherwise they'd collapse onto the
    // selection and "is it back at full range?" could never be true again.
    const [absMin, setAbsMin] = useState(stats?.min ?? 0);
    const [absMax, setAbsMax] = useState(stats?.max ?? 100);
    useEffect(() => {
        if (hasActiveFilter) return;
        if (stats?.min !== undefined) setAbsMin(stats.min);
        if (stats?.max !== undefined) setAbsMax(stats.max);
    }, [stats?.min, stats?.max, hasActiveFilter]);

    const appliedMin = value.min ?? absMin;
    const appliedMax = value.max ?? absMax;

    // The live thumb positions. Kept apart from `value` so dragging stays smooth
    // while the debounced query catches up.
    const [draft, setDraft] = useState({ min: appliedMin, max: appliedMax });
    const [minText, setMinText] = useState(format(appliedMin));
    const [maxText, setMaxText] = useState(format(appliedMax));

    // Adopt whatever the panel settled on (a preset, a clear, a workspace
    // switch), but not while the user is mid-drag — `editing` guards that.
    const editing = useRef(false);
    useEffect(() => {
        if (editing.current) return;
        setDraft({ min: appliedMin, max: appliedMax });
        setMinText(format(appliedMin));
        setMaxText(format(appliedMax));
        // `format` is a stable module function per call site.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedMin, appliedMax]);

    const timer = useRef(null);
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    const commit = useCallback((min, max, { immediate = false } = {}) => {
        if (timer.current) clearTimeout(timer.current);
        const run = () => {
            editing.current = false;
            // Back at both extremes is not a filter — drop it rather than send a
            // range that matches everything and still shows as an active chip.
            if (min <= absMin && max >= absMax) onClear?.(field);
            else onApply?.(field, min, max);
        };
        if (immediate) run();
        else timer.current = setTimeout(run, APPLY_DELAY_MS);
    }, [absMin, absMax, field, onApply, onClear]);

    // Sliders commit on change (debounced) rather than on mouseup, so holding an
    // arrow key filters exactly like dragging does.
    const moveMin = (raw) => {
        const next = Math.min(Number(raw), draft.max);
        editing.current = true;
        setDraft((d) => ({ ...d, min: next }));
        setMinText(format(next));
        commit(next, draft.max);
    };

    const moveMax = (raw) => {
        const next = Math.max(Number(raw), draft.min);
        editing.current = true;
        setDraft((d) => ({ ...d, max: next }));
        setMaxText(format(next));
        commit(draft.min, next);
    };

    const commitMinText = () => {
        const parsed = parse(minText);
        if (parsed === null) { setMinText(format(draft.min)); return; }  // unreadable → revert
        const next = Math.max(absMin, Math.min(parsed, draft.max));
        setDraft((d) => ({ ...d, min: next }));
        setMinText(format(next));
        commit(next, draft.max, { immediate: true });
    };

    const commitMaxText = () => {
        const parsed = parse(maxText);
        if (parsed === null) { setMaxText(format(draft.max)); return; }
        const next = Math.min(absMax, Math.max(parsed, draft.min));
        setDraft((d) => ({ ...d, max: next }));
        setMaxText(format(next));
        commit(draft.min, next, { immediate: true });
    };

    const applyPreset = (preset) => {
        const min = Math.max(absMin, preset.min ?? absMin);
        const max = Math.min(absMax, preset.max ?? absMax);
        editing.current = false;
        setDraft({ min, max });
        setMinText(format(min));
        setMaxText(format(max));
        commit(min, max, { immediate: true });
    };

    const reset = () => {
        editing.current = false;
        setDraft({ min: absMin, max: absMax });
        setMinText(format(absMin));
        setMaxText(format(absMax));
        if (timer.current) clearTimeout(timer.current);
        onClear?.(field);
    };

    // No stats means Algolia returned no numeric range for this attribute —
    // nothing to filter over, so the section would be an empty slider.
    if (!stats) return null;

    const range = absMax - absMin;
    const pct = (v) => (range === 0 ? 0 : ((v - absMin) / range) * 100);

    // Whichever thumb sits where the other one has to be reached THROUGH goes
    // underneath. Proportional, so it holds for seconds and for gigabytes alike.
    const minThumbOnTop = range > 0 && (draft.min - absMin) / range > 0.6;

    const activePreset = presets.find(
        (p) => (p.min ?? absMin) === value.min && (p.max ?? absMax) === value.max,
    );

    return (
        <div className="asp-facet-section">
            <FacetSectionHeader
                title={title}
                expanded={expanded}
                onToggle={() => setExpanded((e) => !e)}
                count={hasActiveFilter ? 1 : 0}
            />

            {expanded && (
                <div className="asp-facet-body">
                    {presets.length > 0 && (
                        <div className="asp-range-presets">
                            {presets.map((preset) => (
                                <button
                                    key={preset.label}
                                    type="button"
                                    className={`asp-range-preset ${activePreset === preset ? "is-active" : ""}`.trim()}
                                    aria-pressed={activePreset === preset}
                                    onClick={() => (activePreset === preset ? reset() : applyPreset(preset))}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="asp-range-inputs">
                        <input
                            type="text"
                            inputMode="decimal"
                            className="asp-range-input"
                            value={minText}
                            aria-label={`Minimum ${title.toLowerCase()}`}
                            onChange={(e) => setMinText(e.target.value)}
                            onBlur={commitMinText}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitMinText(); }
                                if (e.key === "Escape") { e.stopPropagation(); setMinText(format(draft.min)); }
                            }}
                        />
                        <span className="asp-range-dash" aria-hidden="true">–</span>
                        <input
                            type="text"
                            inputMode="decimal"
                            className="asp-range-input"
                            value={maxText}
                            aria-label={`Maximum ${title.toLowerCase()}`}
                            onChange={(e) => setMaxText(e.target.value)}
                            onBlur={commitMaxText}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitMaxText(); }
                                if (e.key === "Escape") { e.stopPropagation(); setMaxText(format(draft.max)); }
                            }}
                        />
                    </div>

                    <div className="asp-range-slider">
                        <div className="asp-range-track" />
                        <div
                            className="asp-range-fill"
                            style={{ left: `${pct(draft.min)}%`, width: `${pct(draft.max) - pct(draft.min)}%` }}
                        />
                        <input
                            type="range"
                            className="asp-range-thumb"
                            min={absMin}
                            max={absMax}
                            value={draft.min}
                            style={{ zIndex: minThumbOnTop ? 4 : 2 }}
                            aria-label={`Minimum ${title.toLowerCase()}`}
                            aria-valuetext={format(draft.min)}
                            onChange={(e) => moveMin(e.target.value)}
                        />
                        <input
                            type="range"
                            className="asp-range-thumb"
                            min={absMin}
                            max={absMax}
                            value={draft.max}
                            style={{ zIndex: 3 }}
                            aria-label={`Maximum ${title.toLowerCase()}`}
                            aria-valuetext={format(draft.max)}
                            onChange={(e) => moveMax(e.target.value)}
                        />
                    </div>

                    <div className="asp-range-foot">
                        <span className="asp-range-bounds">
                            {format(absMin)} – {format(absMax)}
                        </span>
                        {hasActiveFilter && (
                            <button type="button" className="asp-range-reset" onClick={reset}>
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
